'use client';

import { useState } from 'react';
import {
  TrendingUp,
  Package,
  AlertCircle,
  Users,
  FileText,
  ShoppingCart,
  Receipt,
  ClipboardList,
  FileWarning,
} from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { formatCurrency } from '../../../types';
import {
  suppliers,
  purchaseOrders,
  purchaseInvoices,
  materialRequests,
  getBuyingStats,
} from '../data/mock-data';
import {
  purchaseOrderStatusConfig,
  purchaseInvoiceStatusConfig,
  materialRequestStatusConfig,
  materialRequestTypeConfig,
} from '../types';
import type { BuyingPage } from '../index';

type TabType = 'requests' | 'overdue';

interface BuyingDashboardProps {
  onNavigate: (page: BuyingPage) => void;
}

export function BuyingDashboard({ onNavigate }: BuyingDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('requests');
  const stats = getBuyingStats();

  // 최근 발주서 (최근 5개)
  const recentOrders = purchaseOrders
    .filter((po) => po.status !== 'draft')
    .slice(0, 5);

  // 대기중인 자재요청
  const pendingRequests = materialRequests
    .filter((mr) => mr.status === 'pending' || mr.status === 'submitted')
    .slice(0, 5);

  // 연체 청구서
  const overdueInvoices = purchaseInvoices.filter((pi) => pi.status === 'overdue');

  // 주요 공급업체 (구매금액 기준 상위 5개)
  const topSuppliers = [...suppliers]
    .filter((s) => s.isActive)
    .sort((a, b) => b.totalPurchase - a.totalPurchase)
    .slice(0, 5);

  const orderColumns = [
    {
      key: 'code',
      label: '발주번호',
      width: '25%',
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'supplierName',
      label: '공급업체',
      width: '30%',
    },
    {
      key: 'grandTotal',
      label: '금액',
      width: '25%',
      align: 'right' as const,
      render: (value: unknown) => formatCurrency(value as number),
    },
    {
      key: 'status',
      label: '상태',
      width: '20%',
      render: (value: unknown) => {
        const status = value as keyof typeof purchaseOrderStatusConfig;
        const config = purchaseOrderStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
  ];

  const requestColumns = [
    {
      key: 'code',
      label: '요청번호',
      width: '30%',
      render: (value: unknown) => (
        <span className="font-mono text-sm">{String(value)}</span>
      ),
    },
    {
      key: 'type',
      label: '유형',
      width: '20%',
      render: (value: unknown) => {
        const type = value as keyof typeof materialRequestTypeConfig;
        const config = materialRequestTypeConfig[type];
        const colorClasses: Record<string, string> = {
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'requestedBy',
      label: '요청자',
      width: '30%',
    },
    {
      key: 'status',
      label: '상태',
      width: '20%',
      render: (value: unknown) => {
        const status = value as keyof typeof materialRequestStatusConfig;
        const config = materialRequestStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">이번 달 구매</p>
              <p className="text-xl font-bold mt-1 truncate">
                {formatCurrency(stats.totalPurchaseAmount)}
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">진행중 발주</p>
              <p className="text-xl font-bold mt-1">{stats.pendingOrders}건</p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <Package className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">미지급금</p>
              <p className="text-xl font-bold mt-1 text-red-500 truncate">
                {formatCurrency(stats.totalOutstanding)}
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">활성 공급업체</p>
              <p className="text-xl font-bold mt-1">
                {stats.activeSuppliers}
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  / {stats.totalSuppliers}
                </span>
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 주요 공급업체 */}
        <div className="lg:col-span-1 p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">주요 공급업체</h3>
            <button
              onClick={() => onNavigate('suppliers')}
              className="text-sm text-primary hover:underline"
            >
              전체보기
            </button>
          </div>
          <div className="space-y-3">
            {topSuppliers.map((supplier, index) => (
              <div
                key={supplier.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{supplier.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {supplier.contactPerson}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">
                    {formatCurrency(supplier.totalPurchase)}
                  </div>
                  {supplier.outstandingAmount > 0 && (
                    <div className="text-xs text-red-500">
                      미지급 {formatCurrency(supplier.outstandingAmount)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 최근 발주서 */}
        <div className="lg:col-span-2 p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">최근 발주서</h3>
            <button
              onClick={() => onNavigate('orders')}
              className="text-sm text-primary hover:underline"
            >
              전체보기
            </button>
          </div>
          <DataTable
            data={recentOrders}
            columns={orderColumns}
            pageSize={5}
            showPagination={false}
            emptyMessage="발주서가 없습니다."
          />
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg">
        {/* 탭 헤더 */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'requests'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              대기중 자재요청
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'requests'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{pendingRequests.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('overdue')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'overdue'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <FileWarning className="w-4 h-4" />
              연체 청구서
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'overdue'
                  ? 'bg-red-500 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}>{overdueInvoices.length}</span>
            </button>
          </div>
          <button
            onClick={() => onNavigate(activeTab === 'requests' ? 'requests' : 'invoices')}
            className="text-sm text-primary hover:underline flex items-center gap-1 pr-4"
          >
            전체보기
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-4">
          {activeTab === 'requests' ? (
            pendingRequests.length > 0 ? (
              <DataTable
                data={pendingRequests}
                columns={requestColumns}
                pageSize={5}
                showPagination={false}
              />
            ) : (
              <div className="text-center text-muted-foreground py-8">
                대기중인 자재요청이 없습니다.
              </div>
            )
          ) : overdueInvoices.length > 0 ? (
            <div className="space-y-3">
              {overdueInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30"
                >
                  <div>
                    <div className="font-medium">{invoice.code}</div>
                    <div className="text-sm text-muted-foreground">
                      {invoice.supplierName}
                    </div>
                    <div className="text-xs text-red-500">
                      마감일: {invoice.dueDate}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-red-500">
                      {formatCurrency(invoice.outstandingAmount)}
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      {purchaseInvoiceStatusConfig[invoice.status].label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              연체된 청구서가 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 빠른 작업 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">빠른 작업</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate('orders')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <ShoppingCart className="w-6 h-6 text-blue-500" />
            <span className="text-sm font-medium">발주서 작성</span>
          </button>
          <button
            onClick={() => onNavigate('receipts')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <Package className="w-6 h-6 text-green-500" />
            <span className="text-sm font-medium">입고 처리</span>
          </button>
          <button
            onClick={() => onNavigate('invoices')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <Receipt className="w-6 h-6 text-purple-500" />
            <span className="text-sm font-medium">청구서 등록</span>
          </button>
          <button
            onClick={() => onNavigate('requests')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <ClipboardList className="w-6 h-6 text-orange-500" />
            <span className="text-sm font-medium">자재요청</span>
          </button>
        </div>
      </div>
    </div>
  );
}
